import {
  AdvertiserType,
  AdvertisersPayload,
  ExistingAmcInstance,
  Profile,
  useConnectExistingAmcInstanceMutation,
  useCreateAmcInstanceMutation,
  useFetchAMCProfilesMutation,
  useFetchingExistingAmcInstanceMutation,
} from "~/store/connections/connectAMC.redux";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React, { ChangeEvent, useState } from "react";
import {
  countries,
  generateAmazonAdvertisingAuthorizationURL,
  getRegion,
  topCountries,
} from "~/pages/marketplaceConnectors/amazonAdvertisingSelectCountry";

import AlertCard from "~/components/alert/alertCard";
import { AmazonCountry } from "mm-amazon-common/dist/typedef/mws";
import { GenericSelect } from "~/components/select/genericSelect";
import { InlineIconButton } from "~/components/totals/genericTotal";
import Medium from "~/components/typography/medium";
import MultiSelect from "~/components/select/multiSelectDropDown";
import NativeSelect from "~/components/nativeSelect/nativeSelect";
import RaisedButton from "~/components/buttons/raisedButton";
import styled from "styled-components";
import useAmazonAuthcodeAdvertisingAuth from "~/hooks/useAmazonAuthcodeAdvertisingAuth";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

const WrapperGrid = styled(Grid)`
  width: 100%;
  max-width: 350px;
  position: relative;
`;

const CenteredFormControlLabel = styled(FormControlLabel)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const ErrorMessage = styled(Typography)`
  min-height: 1.5em;
`;

const LinearProgressBar = styled(LinearProgress)`
  width: 100%;
  margin-top: 16px;
  margin-bottom: 16px;
`;

enum STAGE {
  INITIAL,
  NEW_INSTANCE,
  SELECT_INSTANCE,
  NEW_INSTANCE_SUCCESS,
  EXISTING_INSTANCE_SUCCESS,
}

export interface ConnectionProps {
  onSuccess: () => void;
}

const AmcConnectionPrompt: React.FC<ConnectionProps> = ({ onSuccess }) => {
  const { t } = useTranslation();

  const [countryCode, setCountryCode] = useState<string>("");
  const [amcName, setAmcName] = useState<string>("");
  const [amcAccountCountry, setAmcAccountCountry] = useState<string>("");
  const [amcAccountId, setAmcAccountId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [instanceNameError, setInstanceNameError] = useState<string | null>(
    null
  );
  const [isExistingAmcInstance, setIsExistingAmcInstance] = useState(false);
  const [stage, setStage] = useState<STAGE | null>(STAGE.INITIAL);

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [selectedDSPValues, setSelectedDSPValues] = useState<string[]>([]);

  const [selectedExistingInstance, setSelectedExistingInstance] =
    useState<ExistingAmcInstance | null>(null);

  const amcExistingInstanceFlagOn = useTypedSelector(
    (state) => state.user.amcExistingInstanceOn ?? false
  );

  const [
    fetchAmcProfiles,
    { data: profilesData, isLoading: fetchProfilesLoading },
  ] = useFetchAMCProfilesMutation();

  const [
    fetchExistingAmcInstance,
    { existingInstances, isLoading: existingInstanceLoading },
  ] = useFetchingExistingAmcInstanceMutation({
    selectFromResult: ({ data, isLoading }) => {
      return {
        existingInstances: data?.instances ?? [],
        isLoading,
      };
    },
  });

  const [createAmcInstance, { isLoading: createInstanceLoading }] =
    useCreateAmcInstanceMutation();

  const [connectExistingAmcInstance, { isLoading: connectInstanceLoading }] =
    useConnectExistingAmcInstanceMutation();

  const handleMultiSelectChange = (values: string[]) => {
    setSelectedValues(values);
  };

  const handleMultiSelectChangeDSP = (values: string[]) => {
    setSelectedDSPValues(values);
  };

  //use for existing instance connection
  const handleLinkExistingAMC = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsExistingAmcInstance(isChecked);
  };

  const getProfileById = (
    id: string,
    profiles: Profile[],
    type: AdvertiserType
  ): AdvertisersPayload => {
    const profile = profiles.find((profile) => profile.entityId === id);
    return {
      id,
      type,
      marketplaceId: profile?.marketplaceId ?? null,
      mid: profile?.mid ?? "",
      marketplaceType: profile?.marketplaceType ?? "",
    };
  };

  const handleCountryChange = (
    e: ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const selectedCountry = [...topCountries, ...countries].find(
      (country) => country.value === e.target.value
    );

    if (selectedCountry) {
      setCountryCode(selectedCountry.value);
    }
  };

  const handleAMCInstanceName = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.match(/^[a-zA-Z0-9\s]*$/) || value === "") {
      setAmcName(value);
    }
    const valueLength = value.trim().length;
    if (valueLength < 3 || valueLength > 50) {
      setInstanceNameError(t("connectWizard.amcInstanceNameError"));
    } else {
      setInstanceNameError(null);
    }
  };

  const handleAmcAccountCountryChange = (
    e: ChangeEvent<{ name?: string; value: unknown }>
  ) => {
    const selectedCountry = [...topCountries, ...countries].find(
      (country) => country.value === e.target.value
    );
    if (selectedCountry) {
      setAmcAccountCountry(selectedCountry.value);
    }
  };

  const handleAmcAccountIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAmcAccountId(e.target.value);
  };

  const handleClickNext = async () => {
    try {
      await fetchAmcProfiles({}).unwrap();
      setStage(STAGE.NEW_INSTANCE);
    } catch (error) {
      setError("Error when fetching advertisers");
    }
  };

  const handleCreateInstance = async () => {
    const advertisersPayload: AdvertisersPayload[] = [
      ...selectedValues.map((id: string) =>
        getProfileById(
          id,
          profilesData?.amcAdProfiles ?? [],
          AdvertiserType.SPONSORED_ADS
        )
      ),
      ...selectedDSPValues.map((id: string) =>
        getProfileById(
          id,
          profilesData?.amcDspProfiles ?? [],
          AdvertiserType.DISPLAY
        )
      ),
    ];

    if (advertisersPayload.length > 10) {
      setError(t("connectWizard.amcTooManyAdvertisersError"));
      return;
    }

    try {
      await createAmcInstance({
        instanceName: amcName.trim(),
        countryCode,
        advertisersPayload,
      }).unwrap();
      setError(null);
      setStage(STAGE.NEW_INSTANCE_SUCCESS);
      onSuccess();
    } catch (error) {
      setError(
        "Error creating a new AMC instance. Please try again and contact support if the problem persists."
      );
    }
  };

  /* Existing Instance Authorization */
  const onCode = async (code: string) => {
    const { instances } = await fetchExistingAmcInstance({
      authCode: code,
      instanceName: amcName,
      countryCode,
      amcAccountCountry,
      amcAccountId,
    }).unwrap();
    if (instances.length === 0) {
      setError(t("connectWizard.amcNoInstancesError"));
    } else {
      setStage(STAGE.SELECT_INSTANCE);
    }
  };
  const region = getRegion(countryCode as AmazonCountry); // Will be NA if countryCode is null
  const url = generateAmazonAdvertisingAuthorizationURL(region);
  const triggerSignin = useAmazonAuthcodeAdvertisingAuth(onCode, url);
  const handleSignin = () => {
    if (!amcAccountCountry || !amcAccountId) {
      setError(t("connectWizard.amcConnectorRequiredFields"));
      return;
    }
    setSelectedExistingInstance(null);
    triggerSignin();
  };
  /* Existing Instance Authorization End */

  const handleConnectExistingInstance = async () => {
    try {
      if (!selectedExistingInstance) {
        setError(t("connectWizard.amcConnectorRequiredFields"));
        return;
      }

      await connectExistingAmcInstance({
        ...selectedExistingInstance,
        countryCode,
      }).unwrap();
      setError(null);
      setStage(STAGE.EXISTING_INSTANCE_SUCCESS);
      onSuccess();
    } catch (error) {
      setError(
        "Error connecting to existing AMC instance. Please try again and contact support if the problem persists."
      );
    }
  };

  const renderInitialStage = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box mb={2}>
          <Typography variant="h5" color="textSecondary" noWrap align="center">
            {t("connectWizard.AmcInitialPageTitle")}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Typography variant="body1" color="textSecondary">
            {t("connectWizard.amcAmazonCountryLabel")}
          </Typography>
          <Box pt={1} pb={1} width={1}>
            <NativeSelect
              name="countryCode"
              value={countryCode}
              topOptions={topCountries}
              options={countries}
              onChange={handleCountryChange}
              autoFocus
              fullWidth
            />
          </Box>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Typography variant="body1" color="textSecondary">
            {t("connectWizard.AmcInitialPageNameInput")}
          </Typography>
          <TextField
            fullWidth
            required
            value={amcName}
            onChange={handleAMCInstanceName}
          />
          {instanceNameError && (
            <ErrorMessage color="error">{instanceNameError}</ErrorMessage>
          )}
        </FormControl>
      </Grid>

      {amcExistingInstanceFlagOn ? (
        <>
          <Grid container item xs={12} justifyContent="center">
            <CenteredFormControlLabel
              control={
                <Checkbox
                  checked={isExistingAmcInstance}
                  size="small"
                  color="primary"
                  onChange={handleLinkExistingAMC}
                />
              }
              label={t("connectWizard.AmcLinkAmc")}
            />
          </Grid>
          {isExistingAmcInstance && (
            <>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Typography variant="body1" color="textSecondary">
                    {t("connectWizard.amcAccountCountryLabel")}
                  </Typography>
                  <Box pt={1} pb={1} width={1}>
                    <NativeSelect
                      name="countryCode"
                      value={amcAccountCountry}
                      topOptions={topCountries}
                      options={countries}
                      onChange={handleAmcAccountCountryChange}
                      autoFocus
                      fullWidth
                    />
                  </Box>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Typography variant="body1" color="textSecondary">
                    {t("connectWizard.amcAccountIdLabel")}
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    value={amcAccountId}
                    onChange={handleAmcAccountIdChange}
                  />
                  {instanceNameError && (
                    <ErrorMessage color="error">
                      {instanceNameError}
                    </ErrorMessage>
                  )}
                </FormControl>
              </Grid>
            </>
          )}
        </>
      ) : null}

      {isExistingAmcInstance ? (
        <Grid item xs={12}>
          <RaisedButton
            fullWidth
            disabled={existingInstanceLoading}
            color="primary"
            onClick={handleSignin}
          >
            {t("connectWizard.authorizeButton")}
          </RaisedButton>
        </Grid>
      ) : amcName && countryCode && !instanceNameError ? (
        <Grid item xs={12}>
          <RaisedButton
            fullWidth
            disabled={fetchProfilesLoading}
            color="primary"
            onClick={handleClickNext}
          >
            {t("generic.next")}
          </RaisedButton>
        </Grid>
      ) : null}
    </Grid>
  );

  const renderNewInstanceStage = () => {
    const amazonAdvertisers = profilesData?.amcAdProfiles
      ? profilesData.amcAdProfiles.map((profile: Profile) => ({
          value: profile.entityId,
          label: `${profile.accountName} (${profile.entityId})`,
        }))
      : [];
    const dspAdvertisers = profilesData?.amcDspProfiles
      ? profilesData.amcDspProfiles.map((profile: Profile) => ({
          value: profile.entityId,
          label: `${profile.accountName} (${profile.entityId})`,
        }))
      : [];

    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h5" color="textSecondary" noWrap align="center">
            {t("connectWizard.AmcNewInstanceTitle")}
          </Typography>
          <Grid
            container
            item
            xs={12}
            justifyContent="center"
            alignItems="center"
          >
            <Typography variant="body1" color="textSecondary" align="center">
              {t("connectWizard.AmcNewInstanceDescription")}
            </Typography>
            <Tooltip title={t("connectWizard.amcNewInstanceTooltip")}>
              <InlineIconButton />
            </Tooltip>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" color="textSecondary">
            {t("connectWizard.AmcAdvertiserSelect")}
          </Typography>
          <MultiSelect
            nonSelectionMsg={t(
              "multiSelectDropdown.amcAdvertiserNonSelectionMsg"
            )}
            currentValues={selectedValues}
            handleChange={handleMultiSelectChange}
            options={amazonAdvertisers}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" color="textSecondary">
            {t("connectWizard.AmcDSPAdvertiserSelect")}
          </Typography>
          <MultiSelect
            nonSelectionMsg={t("multiSelectDropdown.amcDspNonSelectionMsg")}
            currentValues={selectedDSPValues}
            handleChange={handleMultiSelectChangeDSP}
            options={dspAdvertisers}
          />
        </Grid>
        {selectedValues.length > 0 || selectedDSPValues.length > 0 ? (
          <Grid item xs={12}>
            <RaisedButton
              fullWidth
              disabled={createInstanceLoading}
              color="primary"
              onClick={handleCreateInstance}
            >
              {t("connectWizard.AmcCreateBtn")}
            </RaisedButton>
          </Grid>
        ) : null}
      </>
    );
  };

  const renderSelectInstanceStage = () => {
    const existingInstanceOptions = existingInstances.map((instance) => ({
      name: instance.instanceId,
      label: `${instance.instanceName} (${instance.instanceId})`,
    }));

    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h5" color="textSecondary" noWrap align="center">
            {t("connectWizard.amcSelectInstanceTitle")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <GenericSelect
            id="existing-instance-select"
            title=""
            currentOption={existingInstanceOptions.find(
              (opt) => opt.name === selectedExistingInstance?.instanceId
            )}
            setOption={(opt) => {
              setSelectedExistingInstance(
                existingInstances.find(
                  (instance) => instance.instanceId === opt.name
                ) ?? null
              );
            }}
            options={existingInstanceOptions}
            placeholderLabel={t("connectWizard.amcSelectInstanceTitle")}
          />
        </Grid>
        {selectedExistingInstance ? (
          <Grid item xs={12}>
            <RaisedButton
              fullWidth
              disabled={connectInstanceLoading}
              color="primary"
              onClick={handleConnectExistingInstance}
            >
              {t("connectWizard.amcConnectInstance")}
            </RaisedButton>
          </Grid>
        ) : null}
      </>
    );
  };

  return (
    <WrapperGrid container alignItems="center">
      {(fetchProfilesLoading ||
        createInstanceLoading ||
        existingInstanceLoading ||
        connectInstanceLoading) && <LinearProgressBar />}

      <Grid
        container
        item
        alignItems="center"
        justifyContent="center"
        xs={12}
        spacing={2}
      >
        {stage === STAGE.INITIAL && renderInitialStage()}
        {stage === STAGE.NEW_INSTANCE && renderNewInstanceStage()}
        {stage === STAGE.SELECT_INSTANCE && renderSelectInstanceStage()}

        {error && (
          <Grid
            container
            item
            xs={12}
            alignItems="center"
            justifyContent="center"
          >
            <ErrorMessage color="error">{error}</ErrorMessage>
          </Grid>
        )}

        {stage === STAGE.NEW_INSTANCE_SUCCESS && (
          <Grid item xs={12}>
            <AlertCard isOpen type="success">
              <Medium variant="body1">
                {t("connectWizard.AmcCongratulationsBanner")}
              </Medium>
            </AlertCard>
          </Grid>
        )}

        {stage === STAGE.EXISTING_INSTANCE_SUCCESS && (
          <Grid item xs={12}>
            <AlertCard isOpen type="success">
              <Medium variant="body1">
                {t("connectWizard.amcExistingInstanceSuccessBanner")}
              </Medium>
            </AlertCard>
          </Grid>
        )}
      </Grid>
    </WrapperGrid>
  );
};
export default AmcConnectionPrompt;
